import React, { createContext, FC, ReactNode, useState } from 'react';
import { LoginSuccessResponse } from '@services/useLogin';
import { RegistrationFirstStepSuccessResponse } from '@services/useRegistrationFirstStep';
import { RegistrationCarInfoStepSuccessResponse } from '@services/useRegistrationCarInfoStep';
import { AvailableDriversSuccessResponse } from '@services/useGetAllDrivers';
import { GetAllSettingsSuccessResponse } from '@services/useGetAllSettings';
import { GetUserSuccessResponse } from '@services/useGetUser';
import { useEndpointState } from '@hooks/useEndpointState';
import { RoleSuccessResponse } from '@services/useUserTypes';
import { Role, LastLocationSaved, LastMapLocation } from '@types';
import { Language, DEFAULT_LANGUAGE, isSupportedLanguage } from '@constants';
import { google2FAQrCodeSuccessResponse } from '@services/useGoogle2FAQrCode';

export type Draft = {
    uuid: string | null;
    email: string | null;
    current_step: number | null;
};

type OtpStep = {
    verified: boolean;
    timestamp: number;
    uuid?: string | null;
};

type EndpointsConfig = {
    [key: string]: {
        // if true, data will be stored/persisted on localStorage, and still be available if user refreshes the page
        // some endpoints don't need to be persisted (example: paginated product results)
        storeOnLocalStorage: boolean;

        // if storeOnLocalStorage is true, we can also set:

        // TTL (time-to-live) in milliseconds
        // if set, data will be cleared from localStorage after ttl
        ttl?: number | null;
        // if true, data will be cleared from localStorage after browser session changes
        // or when user logs in, or when user logs out
        clearOnNewSession?: boolean;
        // if true, data will be cleared from localStorage after user selects a different language
        clearOnLanguageChange?: boolean;
        // if set, persisted data that fails this check is discarded on load
        validate?: (value: unknown) => boolean;
    };
};

const ENDPOINTS: EndpointsConfig = {
    roles: { storeOnLocalStorage: true, ttl: null },
    selectedRole: { storeOnLocalStorage: true, ttl: null },
    draft: { storeOnLocalStorage: true, ttl: null },
    firstStep: { storeOnLocalStorage: true, ttl: null },
    otpStep: { storeOnLocalStorage: true, ttl: null },
    carInfoStep: { storeOnLocalStorage: true, ttl: null },
    login: { storeOnLocalStorage: true },
    register: { storeOnLocalStorage: true },
    user: { storeOnLocalStorage: true },
    qrCode: { storeOnLocalStorage: true },
    lastLocationSaved: { storeOnLocalStorage: true, ttl: 120000, clearOnNewSession: true }, // 2 minutes
    availableDrivers: { storeOnLocalStorage: false, ttl: null },
    language: { storeOnLocalStorage: true, ttl: null, validate: isSupportedLanguage },
    lastMapLocation: { storeOnLocalStorage: true, ttl: null },
    allSettings: {
        storeOnLocalStorage: true,
        ttl: 120000, // 2 minutes
        clearOnNewSession: true,
    },
};

type DataContextProps = {
    roles: RoleSuccessResponse | null;
    setRoles: (data: RoleSuccessResponse | null) => void;

    selectedRole: Role | null;
    setSelectedRole: (role: Role | null) => void;

    login: LoginSuccessResponse | null;
    setLogin: (data: LoginSuccessResponse | null) => void;

    register: boolean;
    setRegister: (data: boolean) => void;

    qrCode: google2FAQrCodeSuccessResponse | null;
    setQrCode: (data: google2FAQrCodeSuccessResponse | null) => void;

    draft: Draft | null;
    setDraft: (data: Draft | null) => void;

    firstStep: RegistrationFirstStepSuccessResponse | null;
    setFirstStep: (data: RegistrationFirstStepSuccessResponse | null) => void;

    otpStep: OtpStep | null;
    setOtpStep: (data: OtpStep | null) => void;

    carInfoStep: RegistrationCarInfoStepSuccessResponse | null;
    setCarInfoStep: (data: RegistrationCarInfoStepSuccessResponse | null) => void;

    user: GetUserSuccessResponse | null;
    setUser: (data: GetUserSuccessResponse | null) => void;

    lastLocationSaved: LastLocationSaved | null;
    setLastLocationSaved: (data: LastLocationSaved | null) => void;

    language: Language;
    setLanguage: (lang: Language) => void;

    allSettings: GetAllSettingsSuccessResponse | null;
    setAllSettings: (data: GetAllSettingsSuccessResponse) => void;

    availableDrivers: AvailableDriversSuccessResponse | null;
    setAvailableDrivers: (data: AvailableDriversSuccessResponse | null) => void;

    lastMapLocation: LastMapLocation | null;
    setLastMapLocation: (data: LastMapLocation | null) => void;
};

/**
 * DataContext acts as a data center for information retrieved from fetching endpoints.
 */
export const DataContext = createContext<DataContextProps>({
    roles: null,
    setRoles: () => {},

    selectedRole: null,
    setSelectedRole: () => {},

    login: null,
    setLogin: () => {},

    register: false,
    setRegister: () => {},

    qrCode: null,
    setQrCode: () => {},

    draft: null,
    setDraft: () => {},

    firstStep: null,
    setFirstStep: () => {},

    otpStep: null,
    setOtpStep: () => {},

    carInfoStep: null,
    setCarInfoStep: () => {},

    user: null,
    setUser: () => {},

    lastLocationSaved: null,
    setLastLocationSaved: () => {},

    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},

    allSettings: null,
    setAllSettings: () => {},

    availableDrivers: null,
    setAvailableDrivers: () => {},

    lastMapLocation: null,
    setLastMapLocation: () => {},
});

type DataContextProvider = {
    children: ReactNode;
};

export const DataProvider: FC<DataContextProvider> = ({ children }) => {
    const { setEndpointState, getEndpointState } = useEndpointState(ENDPOINTS);
    const { data: roles } = getEndpointState('roles');
    const { data: login } = getEndpointState('login');
    const { data: draft } = getEndpointState('draft');
    const { data: firstStep } = getEndpointState('firstStep');
    const { data: otpStepFromStorage } = getEndpointState('otpStep');
    const { data: carInfoStep } = getEndpointState('carInfoStep');
    const { data: user } = getEndpointState('user');
    const { data: qrCode } = getEndpointState('qrCode');
    const { data: allSettings } = getEndpointState('allSettings');
    const { data: availableDrivers } = getEndpointState('availableDrivers');
    const { data: storedLanguage } = getEndpointState('language');
    const language: Language = isSupportedLanguage(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;

    const [selectedRole, setSelectedRole] = useState<Role | null>(() => {
        const stored = localStorage.getItem('selectedRole');
        return stored ? JSON.parse(stored) : null;
    });
    const [lastLocationSaved, setLastLocationSaved] = useState<LastLocationSaved | null>(null);
    const setLanguage = (lang: Language) => setEndpointState('language', { data: lang });
    const [lastMapLocation, setLastMapLocation] = useState<LastMapLocation | null>(null);

    const [register, setRegister] = useState<boolean>(false);

    const otpStepInitial = otpStepFromStorage ?? {
        verified: false,
        timestamp: Date.now(),
        firstStepId: '',
    };

    const contextValue: DataContextProps = {
        roles: roles || [],
        setRoles: (data) => setEndpointState('roles', { data }),

        selectedRole,
        setSelectedRole,

        login,
        setLogin: (data) => setEndpointState('login', { data }),

        register,
        setRegister,

        qrCode,
        setQrCode: (data) => setEndpointState('qrCode', { data }),

        draft,
        setDraft: (data) => setEndpointState('draft', { data }),

        firstStep,
        setFirstStep: (data) => setEndpointState('firstStep', { data }),

        otpStep: otpStepInitial,
        setOtpStep: (data) => setEndpointState('otpStep', { data }),

        carInfoStep,
        setCarInfoStep: (data) => setEndpointState('carInfoStep', { data }),

        user,
        setUser: (data) => setEndpointState('user', { data }),

        lastLocationSaved,
        setLastLocationSaved,

        language,
        setLanguage,

        allSettings,
        setAllSettings: (data) => setEndpointState('allSettings', { data }),

        availableDrivers,
        setAvailableDrivers: (data) => setEndpointState('availableDrivers', { data }),

        lastMapLocation,
        setLastMapLocation,
    };

    return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};
